import React, { useState, useEffect } from 'react';
import { Navigate, useLocation, useNavigate ,useSearchParams} from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';

const Performance = () => {
  const { userData } = useUser();
  const location = useLocation();
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const token = localStorage.getItem('auth-token');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams] = useSearchParams();
  const batchId = searchParams.get("batchId");
  const playerId = searchParams.get("playerId");
  const [academies, setAcademies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { selectedCenter, setSelectedCenter } = useUser();
  useEffect(() => {
    const fetchPerformanceData = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_PERFORMANCE_FETCH}?batch_id=${batchId}&academy_id=${selectedCenter}&player_id=${playerId}`,
          {
            method: "GET",
            headers: {
              'Authorization': `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();

        if (data.success) {
          const formattedData = data.data.performances.map((performance, index) => ({
            id: index + 1,
            ProgressName: data.data.player?.user?.name || "Unknown",
            BatchName: data.data.batch?.name || "Unknown",
            Month: new Date(performance.year, performance.month - 1).toLocaleString("en-US", {
              month: "long",
            }),
            Year: performance.year.toString(),
          }));

          setAcademies(formattedData);
        } else {
          throw new Error("API response unsuccessful");
        }
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (playerId && batchId) {
      fetchPerformanceData();
    }
  }, [playerId, batchId]);

  const navigate = useNavigate();

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleNewSlider = () => {
    navigate('/newPerformance', {
      state: {
        playerId: playerId,
        batchId: batchId,
        selectedCenter: Number(selectedCenter)
      }
    });
  };

  const handleEdit = (academy) => {
    const monthNumber = new Date(`${academy.Month} 1, ${academy.Year}`).getMonth() + 1;
  
    navigate('/newPerformance', {
      state: {
        playerId: playerId,
        batchId: batchId,
        selectedCenter: Number(selectedCenter),
        selectedYear: academy.Year,
        selectedMonth: Number(monthNumber), 
      }
    });
  };
  const handleView = (academy) => {
    const monthNumber = new Date(`${academy.Month} 1, ${academy.Year}`).getMonth() + 1;
  
    navigate('/viewPerformance', {
      state: {
        playerId: playerId,
        batchId: batchId,
        selectedCenter: Number(selectedCenter),
        selectedYear: Number(academy.Year),
        selectedMonth: Number(monthNumber), 
      }
    });
  };

  const filteredAcademies = academies.filter((academy) =>
    Object.values(academy)
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = filteredAcademies.slice(indexOfFirstRow, indexOfLastRow);
  const totalPages = Math.ceil(filteredAcademies.length / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage((prevPage) => prevPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage((prevPage) => prevPage - 1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };

  const totalEntries = filteredAcademies.length;
  const indexOfFirstEntry = totalEntries > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? Math.min(currentPage * rowsPerPage, totalEntries) : 0;
  return (
    <div className="enrollment-container">
      <div className="breadcrumb">
        <Link to="/batchList" className="breadcrumb-link">Batch List</Link> &gt; Performance
      </div>
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            placeholder=""
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">
            🔍 Search
          </label>
        </div>
        <button
           className="create-button-new"
           onClick={handleNewSlider}
         >
           <span className="create-button__icon">
             <svg
               xmlns="http://www.w3.org/2000/svg"
               viewBox="0 0 24 24"
               width="23"
               height="23"
               className="create-button__svg"
             >
               <path
                 d="M12 5v14M5 12h14"
                 stroke="white"
                 strokeWidth="2.3"
                 strokeLinecap="round"
                 strokeLinejoin="round"
               />
             </svg>
           </span>
           <span className="create-button__label">
           Add Performance
           </span>
         </button>
      </div>

      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}
      {error && <div className="error-message">{error}</div>}
      {!loading && !error && filteredAcademies.length === 0 && (
        <div className="error-message">No data available</div>
      )}

      <div className="payment-list-scroll-container" style={{  overflowY: 'auto' }}>
        <table className="payment-list-table">
          <thead>
            <tr>
              <th className="courtlist-column courtlist-column-action">Player Name</th>
              <th className="courtlist-column courtlist-column-action">Batch Name</th>
              <th className="courtlist-column courtlist-column-action">Month</th>
              <th className="courtlist-column courtlist-column-action">Year</th>
              <th className="courtlist-column courtlist-column-action">Action</th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map((academy) => (
              <tr key={academy.id}>
                <td>{academy.ProgressName}</td>
                <td>{academy.BatchName}</td>
                <td>{academy.Month}</td>
                <td>{academy.Year}</td>
                <td className="action-buttons">
                <button className="edit-btn" onClick={() => handleEdit(academy)}>Edit</button>
                  <button className="delete-btn" onClick={() => handleView(academy)}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Performance;
